import React, { useState, useMemo, useCallback, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { useForecastV3, useBulkSelection, useMonthlyVersions } from '../../context/ForecastV3Context';
import { useForecastData } from '../../hooks/useForecastData';
import { useRoomStatsByMonth, effectiveAvailableRoomsFrom, effectiveOccupiedRoomsFrom } from '../../hooks/useRoomStatsByMonth';
import { unlockMonth as unlockMonthApi, updateActualRollInPreference, updateRoomStatSource } from '../../api';
import { versionQueryKeys, useVersions } from '../../hooks/useVersions';
import { PnLGrid } from './PnLGrid';
import { AccountDetailPanel } from './AccountDetailPanel';
import { DepartmentDetailPanel } from './DepartmentDetailPanel';
import { StatAccountDetailPanel } from './StatAccountDetailPanel';
import { selectStatAccount, useSelectedStatAccountId } from './otherStatsSession';
import { BulkActionToolbar } from './BulkActionToolbar';
import { BulkDriverEditModal } from './BulkDriverEditModal';
import { GLDetailModal } from './GLDetailModal';
import { DailyEntryModal } from './DailyEntryModal';
import { AiBuildReviewScreen } from '../ai-build/AiBuildReviewScreen';
import { showAiUndoToast } from '../ai-build/aiUndoToast';
import { RevenueForecastModal } from './RevenueForecastModal';
import { GridSkeleton } from '../shared/Skeleton';
import { CreateVersionModal } from '../versions/CreateVersionModal';
import { GitBranch, Plus, ExternalLink, PanelRightClose } from 'lucide-react';
import { PopoutWindow, preopenPopoutWindow } from '../shared/PopoutWindow';
import type { DriverType, ForecastAccount, BulkDriverResponse } from '../../types';
import { MONTHS } from '../../types';
import { useAssistantDataRefresh } from '../../../../assistant/context';

interface WorkspacePageProps {
  locationCurrency?: string | null;
  locationName?: string;
  /** Opens the AI chat seeded with a forecast-logic walkthrough prompt */
  onAnalyzeWithAi?: () => void;
  onBuildDriversWithAi?: () => void;
  onCompareToOtbWithAi?: () => void;
  /** Read-only audit of the (completed) budget version — budget mode only */
  onReviewBudgetWithAi?: () => void;
}

export function WorkspacePage({ locationCurrency = 'USD', locationName, onAnalyzeWithAi, onBuildDriversWithAi, onCompareToOtbWithAi, onReviewBudgetWithAi }: WorkspacePageProps) {
  const { state, selectAccount, updateSelectedAccountData, setVersion, setPendingAccountId, setPendingDetailPanelTab, setDetailPanelTab, setPendingDepartment } = useForecastV3();
  const { selectedAccount, isDetailPanelOpen, orgId, locationId, year, activeMonth, currentVersion, pendingAccountId, pendingDetailPanelTab, pendingDepartmentName, pendingDepartmentSection } = state;
  const queryClient = useQueryClient();

  // Version creation (for no-version empty state)
  const [showCreateModal, setShowCreateModal] = useState(false);
  const { versions, createVersion, isLoading: isLoadingVersions } = useVersions({
    orgId: orgId ?? 0,
    locationId: locationId ?? 0,
    year,
    enabled: !!orgId && !!locationId,
  });

  // Month locking/unlocking
  const { updateLockedMonths, lockedMonths, getMonthStatus, setActiveMonth } = useMonthlyVersions();
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [unlockConfirmMonth, setUnlockConfirmMonth] = useState<number | null>(null);

  // Refresh state
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshSuccess, setRefreshSuccess] = useState(false);

  // Bulk selection state
  const {
    isBulkMode,
    selectedAccountIds,
    selectedCount,
    bulkOperationInProgress,
    toggleBulkMode,
    toggleAccountSelection,
    setSelectedAccounts,
    addSelectedAccounts,
    clearSelection,
    setBulkOperationInProgress,
  } = useBulkSelection();

  // Modal state
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);

  // Build with AI takeover state
  const [showAiBuild, setShowAiBuild] = useState(false);

  // Room stat source state - initialize from version or default to 'pms'
  const [roomStatSource, setRoomStatSource] = useState<'pms' | 'budget' | 'actuals'>('pms');
  const [actualRollInEnabled, setActualRollInEnabled] = useState(true);

  // Initialize roomStatSource from version when it loads/changes
  useEffect(() => {
    console.log('[WorkspacePage] currentVersion roomStatSource:', {
      versionId: currentVersion?.id,
      roomStatSource: currentVersion?.roomStatSource,
      hasProperty: currentVersion ? 'roomStatSource' in currentVersion : false,
    });
    if (currentVersion?.roomStatSource) {
      setRoomStatSource(currentVersion.roomStatSource as 'pms' | 'budget' | 'actuals');
    } else {
      setRoomStatSource('pms');
    }
    // Budget versions always show pure budget values — no actuals blending
    setActualRollInEnabled(currentVersion?.type === 'Budget' ? false : (currentVersion?.actualRollInEnabled ?? true));
  }, [currentVersion?.id, currentVersion?.roomStatSource, currentVersion?.actualRollInEnabled, currentVersion?.type]);

  // Scroll-to state: set when navigating from Notes page via eye icon
  const [scrollToAccountId, setScrollToAccountId] = useState<number | null>(null);

  // Daily entry request state - allows AccountDetailPanel to open DailyEntryModal in PnLGrid
  const [laborModalRequest, setLaborModalRequest] = useState<{ chartOfAccountId: number; nonce: number } | null>(null);
  const [dailyEntryRequest, setDailyEntryRequest] = useState<{
    account: ForecastAccount;
    month: number;
  } | null>(null);

  // GL detail modal state
  const [glDetailModal, setGlDetailModal] = useState<{
    chartOfAccountId: number;
    accountName: string;
    accountNumber: string;
    month: number;
    columnType: 'Actual' | 'LY';
  } | null>(null);

  // Detail pane popped out to its own window (portal — same React tree).
  const [panePoppedOut, setPanePoppedOut] = useState(false);
  // Open the child window synchronously in the click so the browser grants a
  // real popup window (an effect-time window.open lands in a new tab).
  const togglePanePopout = useCallback(() => {
    if (!panePoppedOut) preopenPopoutWindow();
    setPanePoppedOut(!panePoppedOut);
  }, [panePoppedOut]);
  // Daily entry launched from the popped-out pane renders in the popout, not
  // via PnLGrid (which would put it in the parent window).
  const [popoutDailyEntry, setPopoutDailyEntry] = useState<{ account: ForecastAccount; month: number } | null>(null);

  // Revenue forecast modal state
  const [revenueForecastModal, setRevenueForecastModal] = useState<{
    department: string;
    departmentId?: number;
    initialTab?: 'overview' | 'accounts';
  } | null>(null);


  // Department panel state (mutually exclusive with account panel)
  const [selectedDepartment, setSelectedDepartment] = useState<{
    name: string;
    departmentId?: number;  // FK to chartOfAccountDepartments (if available)
    parentSection: 'Revenue' | 'Expense';
  } | null>(null);

  // Determine if the current version is editable (only draft versions can be edited)
  const isReadOnly = currentVersion ? currentVersion.status !== 'Draft' : false;

  // Budget versions show pure budget values — no actuals blending
  const isBudget = currentVersion?.type === 'Budget';

  // Fetch real data from API
  // When a version is selected, use V3 Calculate API for on-demand calculations
  // For locked months, fetch snapshot data from Cosmos DB
  const {
    accounts,
    revenueAccounts,
    expenseAccounts,
    totals,
    rawRecords,
    closedMonthKeys,
    roomStats: v3RoomStats,
    isLoading,
    isRefetching,
    error,
    updateDriver,
    updateDriversBulk,
    saveValue,
    saveValuesBulk,
    clearMonth,
    refetch,
    isBulkUpdating,
    saveRoomStatOverride,
    saveDriverInputOverrides,
    clearDriverInputOverrides,
  } = useForecastData({
    orgId: orgId ?? 0,
    locationId: locationId ?? 0,
    year: year,
    versionId: currentVersion?.id ?? null, // Pass version for V3 calculations
    requireVersion: true,
    lockedMonths: currentVersion?.lockedMonths ?? [], // Pass locked months to fetch snapshots
    enabled: !!orgId && !!locationId,
    roomStatSource, // Pass room stat source for calculations
    actualRollInEnabled,
  });

  // From the ORN/ADR drill-downs: land on the stat-account mapping for the
  // Rooms department (the one the segments' revenue accounts live in).
  const handleOpenStatMapping = useCallback((dept?: { department: string; departmentId?: number }) => {
    const fallback = revenueAccounts.find(a => /room/i.test(a.department || ''));
    const department = dept?.department || fallback?.department;
    if (!department) { toast.error('No Rooms revenue department found to map stat accounts against'); return; }
    setRevenueForecastModal({ department, departmentId: dept?.departmentId ?? fallback?.departmentId, initialTab: 'accounts' });
  }, [revenueAccounts]);


  // Workflow proposal writes happen outside this component's React Query
  // mutations. Refresh the grid only when the assistant actually changed this
  // property/version, including cross-year Rooms-stat plans.
  useAssistantDataRefresh(
    () => { refetch(); },
    {
      kinds: ['forecast_revenue_stat_month', 'forecast_override', 'forecast_adjustment', 'forecast_driver', 'department_driver', 'room_stat_override'],
      locationId,
      versionId: currentVersion?.id ?? null,
      enabled: !!locationId && !!currentVersion?.id,
    },
  );

  // Keep selectedAccount in sync with fresh data after refetch
  // This ensures the detail panel shows updated values when grid data changes
  // Using updateSelectedAccountData instead of selectAccount to preserve the current tab
  const selectedAccountId = selectedAccount?.chartOfAccountId;
  useEffect(() => {
    if (selectedAccountId && accounts.length > 0) {
      const freshAccount = accounts.find(a => a.chartOfAccountId === selectedAccountId);
      // A source/rate/override can change without changing the annual total.
      // The hook reconstructs account objects on render, so compare their data
      // rather than identity to avoid a selection/render loop.
      if (freshAccount && JSON.stringify(freshAccount) !== JSON.stringify(selectedAccount)) {
        updateSelectedAccountData(freshAccount);
      }
    }
  }, [accounts, selectedAccountId, selectedAccount, updateSelectedAccountData]);

  // Handle pending account selection from URL or Notes page navigation
  // When we have a pending account ID and data is loaded, find and select that account
  useEffect(() => {
    if (pendingAccountId && accounts.length > 0) {
      const targetAccount = accounts.find(a => a.chartOfAccountId === pendingAccountId);
      if (targetAccount) {
        // Select the target account (even if different account was already selected)
        selectAccount(targetAccount);
        // Trigger expand-dept + scroll in PnLGrid
        setScrollToAccountId(pendingAccountId);
        // Apply pending panel tab after account is selected (in next effect)
      }
      // Clear the pending ID regardless of whether we found the account
      setPendingAccountId(null);
    }
  }, [pendingAccountId, accounts, selectAccount, setPendingAccountId]);

  // Apply pending panel tab after account is selected from URL
  // This runs after selectAccount resets the tab to 'driver'
  useEffect(() => {
    if (pendingDetailPanelTab && selectedAccount && isDetailPanelOpen) {
      setDetailPanelTab(pendingDetailPanelTab);
      setPendingDetailPanelTab(null);
    }
  }, [pendingDetailPanelTab, selectedAccount, isDetailPanelOpen, setDetailPanelTab, setPendingDetailPanelTab]);

  // Handle pending department selection from Notes page navigation
  // When we have a pending department name, open the department detail panel
  useEffect(() => {
    if (pendingDepartmentName && pendingDepartmentSection && accounts.length > 0) {
      // Close any open account panel first
      if (selectedAccount) {
        selectAccount(null);
      }
      // Open the department panel
      setSelectedDepartment({
        name: pendingDepartmentName,
        parentSection: pendingDepartmentSection,
      });
      // Clear the pending state
      setPendingDepartment(null);
    }
  }, [pendingDepartmentName, pendingDepartmentSection, accounts.length, selectedAccount, selectAccount, setPendingDepartment]);

  // Get selected account objects from IDs
  const selectedAccountObjects = useMemo(() => {
    return accounts.filter(a => selectedAccountIds.includes(a.chartOfAccountId));
  }, [accounts, selectedAccountIds]);

  // AI workflow stage: only a forecast with no populated monthly values gets
  // the "Build with AI" CTA. Manual is a valid driver for populated forecasts,
  // so driver assignment alone must not make an existing forecast look empty.
  // Once any value exists the slot becomes "Analyze with AI".
  // Budgets never offer Build with AI — they stay on "Analyze with AI" throughout.
  const aiWorkflowMode: 'build' | 'analyze' | 'none' = useMemo(() => {
    if (!currentVersion || accounts.length === 0) return 'none';
    if (isBudget) return 'analyze';
    const hasPopulatedValue = accounts.some(account =>
      Object.values(account.monthlyValues).some(value => Number.isFinite(value) && value !== 0)
    );
    if (!hasPopulatedValue && !isReadOnly) return 'build';
    return 'analyze';
  }, [accounts, currentVersion, isReadOnly, isBudget]);

  // Build department drivers map from accounts for PnLGrid
  // Supports both ID-based (preferred) and name-based (legacy) lookups
  const departmentDrivers = useMemo(() => {
    const map: Record<string, string> = {};
    for (const account of accounts) {
      if (account.departmentDriverType) {
        // Prefer departmentId if available (FK-based), otherwise use department name
        if (account.departmentId) {
          const idKey = account.departmentId.toString();
          if (!map[idKey]) {
            map[idKey] = account.departmentDriverType;
          }
        }
        // Also set by name for backwards compatibility
        if (account.department && !map[account.department]) {
          map[account.department] = account.departmentDriverType;
        }
      }
    }
    return map;
  }, [accounts]);

  // Room stats + the effective ARN/ORN the grid divides by. Shared with the
  // GOP tab so both read identical denominators.
  const roomStats = useRoomStatsByMonth({ v3RoomStats, rawRecords });
  const effectiveAvailableRooms = useMemo(() => effectiveAvailableRoomsFrom(roomStats), [roomStats]);
  const effectiveOccupiedRooms = useMemo(() => effectiveOccupiedRoomsFrom(roomStats), [roomStats]);

  // Other Statistics rows open the same right-hand pane. One thing selected
  // at a time: a stat row closes the money account / department pane, and
  // selecting either of those closes the stat pane.
  const selectedStatAccountId = useSelectedStatAccountId();
  useEffect(() => {
    if (selectedStatAccountId === null) return;
    if (selectedAccount) selectAccount(null);
    setSelectedDepartment((prev) => (prev ? null : prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStatAccountId]);
  useEffect(() => {
    if (selectedAccount || selectedDepartment) selectStatAccount(null);
  }, [selectedAccount, selectedDepartment]);

  // Handle department edit button click
  const handleEditDepartment = useCallback((department: { departmentId?: number; departmentName: string }, parentSection: 'Revenue' | 'Expense') => {
    // Close any open account panel first
    if (selectedAccount) {
      selectAccount(null);
    }
    setSelectedDepartment({ name: department.departmentName, departmentId: department.departmentId, parentSection });
  }, [selectedAccount, selectAccount]);

  // Handle department driver saved - refetch data to update grid
  const handleDepartmentDriverSaved = useCallback(() => {
    refetch();
  }, [refetch]);

  // Refetch whenever the AI assistant applies forecast changes (dispatches TRIGGER_REFRESH)
  useEffect(() => {
    if (state.refreshToken > 0) refetch();
  }, [state.refreshToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle room stat source change - save to server and refetch
  const handleRoomStatSourceChange = useCallback(async (newSource: 'pms' | 'budget' | 'actuals') => {
    setRoomStatSource(newSource);

    // Save to server if we have a version
    if (currentVersion?.id && orgId && locationId) {
      try {
        await updateRoomStatSource({
          versionId: currentVersion.id,
          roomStatSource: newSource,
        });
        // Invalidate versions query so the updated roomStatSource is fetched
        queryClient.invalidateQueries({ queryKey: versionQueryKeys.list(orgId, locationId) });
        queryClient.invalidateQueries({ queryKey: versionQueryKeys.listByYear(orgId, locationId, year) });
      } catch (error) {
        console.error('Failed to save room stat source:', error);
        toast.error('Failed to save room stat source');
      }
    }

    // Refetch data with new source
    refetch();
  }, [currentVersion?.id, orgId, locationId, year, queryClient, refetch]);

  // Handle Actuals + Forecast toggle change - save to version and update local grid immediately
  const handleActualRollInChange = useCallback(async (enabled: boolean) => {
    const previous = actualRollInEnabled;
    setActualRollInEnabled(enabled);

    if (currentVersion?.id && orgId && locationId) {
      try {
        const updatedVersion = await updateActualRollInPreference({
          versionId: currentVersion.id,
          actualRollInEnabled: enabled,
        });

        setVersion({
          ...currentVersion,
          actualRollInEnabled: updatedVersion.actualRollInEnabled ?? enabled,
        });
        queryClient.invalidateQueries({ queryKey: versionQueryKeys.list(orgId, locationId) });
        queryClient.invalidateQueries({ queryKey: versionQueryKeys.listByYear(orgId, locationId, year) });
      } catch (error) {
        console.error('Failed to save actual roll-in preference:', error);
        setActualRollInEnabled(previous);
        toast.error('Failed to save workspace preference');
      }
    }
  }, [actualRollInEnabled, currentVersion, orgId, locationId, year, queryClient, setVersion]);

  // Bulk selection handlers
  const handleSelectAccountsByType = useCallback((type: 'Revenue' | 'Expense') => {
    const accountsOfType = type === 'Revenue' ? revenueAccounts : expenseAccounts;
    const typeAccountIds = accountsOfType.map(a => a.chartOfAccountId);

    // Check current selection state
    const allSelected = typeAccountIds.every(id => selectedAccountIds.includes(id));

    if (allSelected) {
      // Deselect all of this type
      const remaining = selectedAccountIds.filter(id => !typeAccountIds.includes(id));
      setSelectedAccounts(remaining);
    } else {
      // Select all of this type
      addSelectedAccounts(typeAccountIds);
    }
  }, [revenueAccounts, expenseAccounts, selectedAccountIds, setSelectedAccounts, addSelectedAccounts]);

  const handleSelectAccountsByDepartment = useCallback((accountIds: number[]) => {
    // Check current selection state
    const allSelected = accountIds.every(id => selectedAccountIds.includes(id));

    if (allSelected) {
      // Deselect all in department
      const remaining = selectedAccountIds.filter(id => !accountIds.includes(id));
      setSelectedAccounts(remaining);
    } else {
      // Select all in department
      addSelectedAccounts(accountIds);
    }
  }, [selectedAccountIds, setSelectedAccounts, addSelectedAccounts]);

  // Bulk driver update handler
  const handleBulkDriverApply = useCallback(async (
    driverType: string,
    driverInput: number | null,
    selectedDepartments?: string[]
  ): Promise<BulkDriverResponse> => {
    const assignments = selectedAccountIds.map(chartOfAccountId => ({
      chartOfAccountId,
      driverType,
      driverInput,
      selectedDepartments, // Pass selected departments for "% of Selected Revenue" driver
    }));

    setBulkOperationInProgress(true);
    try {
      const result = await updateDriversBulk(assignments);
      // Clear selection on success
      if (result.success && result.failed === 0) {
        clearSelection();
        toggleBulkMode(false);
      }
      return result;
    } finally {
      setBulkOperationInProgress(false);
    }
  }, [selectedAccountIds, updateDriversBulk, clearSelection, toggleBulkMode, setBulkOperationInProgress]);

  // Handle month click - show unlock confirmation for locked months
  const handleMonthClick = useCallback((month: number) => {
    setActiveMonth(month);
    const status = getMonthStatus(month);
    if (status === 'locked') {
      setUnlockConfirmMonth(month);
    }
  }, [getMonthStatus, setActiveMonth]);

  // Handle unlock confirmation
  const handleUnlockMonth = useCallback(async () => {
    if (!currentVersion || !unlockConfirmMonth) return;

    setIsUnlocking(true);
    try {
      const result = await unlockMonthApi(String(currentVersion.id), {
        month: unlockConfirmMonth,
        year: year,
      });

      // Update the context with new locked months array
      updateLockedMonths(result.lockedMonths);

      // Refetch data to get fresh calculations
      refetch();

      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                          'July', 'August', 'September', 'October', 'November', 'December'];
      toast.success(`${monthNames[unlockConfirmMonth - 1]} unlocked successfully`);
      setUnlockConfirmMonth(null);
    } catch (error) {
      console.error('Failed to unlock month:', error);
      toast.error('Failed to unlock month');
    } finally {
      setIsUnlocking(false);
    }
  }, [currentVersion, unlockConfirmMonth, year, updateLockedMonths, refetch]);

  // AI build committed: close takeover, refresh grid, offer an Undo toast
  const handleAiBuildCommitted = useCallback((batchId: string, updated: number) => {
    setShowAiBuild(false);
    refetch();
    showAiUndoToast({
      batchId,
      message: `AI assigned drivers to ${updated} account${updated === 1 ? '' : 's'}`,
      revertedMessage: 'AI driver assignments reverted',
      onReverted: () => refetch(),
    });
  }, [refetch]);

  // Handle refresh - re-fetch all data and recalculate
  const handleRefresh = useCallback(async () => {
    if (isRefreshing) return;

    setIsRefreshing(true);
    setRefreshSuccess(false);

    try {
      await refetch();
      setRefreshSuccess(true);

      // Reset success state after 2 seconds
      setTimeout(() => {
        setRefreshSuccess(false);
      }, 2000);
    } catch (error) {
      console.error('Failed to refresh:', error);
      toast.error('Failed to refresh data');
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, refetch]);

  // Handle driver change (only if not read-only)
  const handleDriverChange = async (driverType: DriverType, input: number | null, selectedDepartments?: string[], clearOverrides?: boolean, fallbackDriverType?: 'Budget' | 'LY Same Month' | null, sourceChartOfAccountIds?: number[], zeroBaseDriverLayers?: import('../../types').ZeroBaseDriverLayer[], laborPositionConfigs?: import('../../types').LaborPositionConfig[]) => {
    if (!selectedAccount || isReadOnly) return;
    try {
      await updateDriver(selectedAccount.chartOfAccountId, driverType, input, selectedDepartments, clearOverrides, fallbackDriverType, sourceChartOfAccountIds, zeroBaseDriverLayers, laborPositionConfigs);
    } catch (error) {
      console.error('Failed to update driver:', error);
    }
  };

  // Handle override change (only if not read-only)
  const handleOverrideChange = async (monthIndex: number, value: number | null) => {
    if (!selectedAccount || isReadOnly) return;
    try {
      if (value !== null) {
        // Save the override value
        await saveValue({
          chartOfAccountId: selectedAccount.chartOfAccountId,
          year: year,
          month: monthIndex + 1,
          forecastAmount: value,
        });
      } else {
        // Clear the override (revert to driver-calculated value)
        await clearMonth(selectedAccount.chartOfAccountId, year, monthIndex + 1);
      }
    } catch (error) {
      console.error('Failed to save/clear override:', error);
      throw error; // Re-throw so the UI can show error
    }
  };

  // Show loading state
  if (isLoading || isLoadingVersions || (!currentVersion && versions.length > 0)) {
    return <GridSkeleton rows={12} />;
  }

  // Show error state
  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center" style={{ background: 'var(--lodge-onyx-50)' }}>
        <div style={{ color: 'var(--color-error)' }}>Error loading data: {error.message}</div>
      </div>
    );
  }

  // No version — prompt user to create one before using the workspace
  if (!currentVersion) {
    return (
      <div className="flex-1 flex items-center justify-center" style={{ background: 'var(--lodge-onyx-50)' }}>
        <div className="text-center max-w-md px-6">
          <div className="mx-auto w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: 'var(--lodge-orange-100)' }}>
            <GitBranch size={24} style={{ color: 'var(--lodge-orange-600)' }} />
          </div>
          <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--lodge-onyx-800)' }}>
            No Forecast Version
          </h3>
          <p className="text-sm mb-6" style={{ color: 'var(--lodge-onyx-500)' }}>
            Create a forecast version to start building your projections for this location.
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 px-5 py-2.5 font-semibold text-sm rounded-lg transition-all"
            style={{ background: 'var(--lodge-orange-400)', color: '#ffffff', border: 'none', boxShadow: 'var(--shadow-orange)' }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-orange-500)'; (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-1px)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-orange-400)'; (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0)'; }}
          >
            <Plus size={16} />
            Create Your First Version
          </button>
        </div>

        <CreateVersionModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSubmit={(params) => createVersion({
            ...params,
            baseVersionId: params.baseVersionId != null ? String(params.baseVersionId) : undefined,
          })}
          existingVersions={versions}
          currentYear={year}
        />
      </div>
    );
  }

  // Handle cell save from grid (only if not read-only)
  const handleCellSave = async (chartOfAccountId: number, month: number, value: number) => {
    if (isReadOnly) return;
    await saveValue({
      chartOfAccountId,
      year: year,
      month,
      forecastAmount: value,
    });
  };

  // Handle bulk paste from grid — single optimistic update + background save
  const handleBulkPaste = async (entries: { chartOfAccountId: number; month: number; value: number }[]) => {
    if (isReadOnly || entries.length === 0) return;
    await saveValuesBulk(entries.map(e => ({
      chartOfAccountId: e.chartOfAccountId,
      year: year,
      month: e.month,
      forecastAmount: e.value,
    })));
  };

  // Handle room stat override (projected ARN/ORN)
  const handleRoomStatOverride = async (month: number, roomStatType: 'availableRooms' | 'occupiedRooms', value: number | null) => {
    if (isReadOnly) return;
    try {
      await saveRoomStatOverride(month, roomStatType, value);
      if (value !== null) {
        toast.success(`${roomStatType === 'availableRooms' ? 'Available rooms' : 'Occupied rooms'} override saved`);
      } else {
        toast.success('Override cleared');
      }
    } catch (error) {
      console.error('Failed to save room stat override:', error);
      toast.error('Failed to save override');
      throw error;
    }
  };

  // Get month name for display
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                      'July', 'August', 'September', 'October', 'November', 'December'];

  return (
    <div className="flex-1 flex min-h-0 overflow-hidden">
      <PnLGrid
        onSelectAccount={(account) => {
          // Close department panel when selecting an account
          if (selectedDepartment) {
            setSelectedDepartment(null);
          }
          selectAccount(account);
        }}
        selectedAccount={selectedAccount}
        revenueAccounts={revenueAccounts}
        expenseAccounts={expenseAccounts}
        totals={totals}
        roomStats={roomStats}
        actualRollInEnabled={actualRollInEnabled}
        onActualRollInChange={handleActualRollInChange}
        isBudget={isBudget}
        closedMonthKeys={closedMonthKeys}
        roomStatSource={roomStatSource}
        onRoomStatSourceChange={handleRoomStatSourceChange}
        onRoomStatOverride={currentVersion && !isReadOnly ? handleRoomStatOverride : undefined}
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
        refreshSuccess={refreshSuccess}
        onCellSave={handleCellSave}
        onBulkPaste={handleBulkPaste}
        isReadOnly={isReadOnly}
        isBulkMode={isBulkMode}
        selectedAccountIds={selectedAccountIds}
        onToggleAccountSelection={toggleAccountSelection}
        onSelectAccountsByType={handleSelectAccountsByType}
        onSelectAccountsByDepartment={handleSelectAccountsByDepartment}
        onToggleBulkMode={currentVersion ? () => toggleBulkMode() : undefined}
        onAiBuild={currentVersion && !isReadOnly ? () => setShowAiBuild(true) : undefined}
        onAiAnalyze={onAnalyzeWithAi}
        onAiBuildDrivers={currentVersion && !isReadOnly ? onBuildDriversWithAi : undefined}
        onAiCompareOtb={currentVersion ? onCompareToOtbWithAi : undefined}
        onAiReviewBudget={currentVersion ? onReviewBudgetWithAi : undefined}
        aiWorkflowMode={aiWorkflowMode}
        onMonthClick={handleMonthClick}
        isRecalculating={isRefetching}
        versionId={currentVersion?.id}
        locationId={locationId ?? undefined}
        year={year}
        currency={locationCurrency}
        locationName={locationName}
        versionName={currentVersion?.name}
        onStatInputsApplied={currentVersion && !isReadOnly ? () => { refetch(); } : undefined}
        onOpenStatMapping={currentVersion ? handleOpenStatMapping : undefined}
        departmentDrivers={departmentDrivers}
        onEditDepartment={handleEditDepartment}
        dailyEntryRequest={dailyEntryRequest}
        onDailyEntryRequestHandled={() => setDailyEntryRequest(null)}
        onGLDetailClick={(chartOfAccountId, accountName, accountNumber, month, columnType) => {
          setGlDetailModal({ chartOfAccountId, accountName, accountNumber, month, columnType });
        }}
        onRevenueForecastSetup={!isReadOnly ? (dept) => {
          setRevenueForecastModal({ department: dept.departmentName, departmentId: dept.departmentId });
        } : undefined}
        scrollToAccountId={scrollToAccountId}
        onScrollToAccountHandled={() => setScrollToAccountId(null)}
        onDriverInputOverrideSave={currentVersion && !isReadOnly ? saveDriverInputOverrides : undefined}
        onOpenLaborConfig={(account) => {
          if (selectedDepartment) setSelectedDepartment(null);
          selectAccount(account);
          setLaborModalRequest(prev => ({ chartOfAccountId: account.chartOfAccountId, nonce: (prev?.nonce ?? 0) + 1 }));
        }}
        onClearDriverInputOverrides={currentVersion && !isReadOnly ? clearDriverInputOverrides : undefined}
      />

      {/* Detail pane — account or department. Inline by default; popped out it
          portals into a separate window but stays in this tree, so edits are
          the parent's state. */}
      {(() => {
        const showAccount = isDetailPanelOpen && selectedAccount && !isBulkMode && !selectedDepartment;
        const showDept = selectedDepartment && !isBulkMode && currentVersion && locationId;
        const showStat = selectedStatAccountId !== null && !isBulkMode && !showAccount && !showDept;
        const pane = showStat ? (
          <StatAccountDetailPanel isPoppedOut={panePoppedOut} onTogglePopout={togglePanePopout} />
        ) : showAccount ? (
          <AccountDetailPanel
            account={selectedAccount}
            accounts={accounts}
            onClose={() => selectAccount(null)}
            onDriverChange={handleDriverChange}
            onOpenDailyEntry={(account, month) => (panePoppedOut ? setPopoutDailyEntry({ account, month }) : setDailyEntryRequest({ account, month }))}
            laborModalRequest={laborModalRequest}
            onOverrideChange={handleOverrideChange}
            isReadOnly={isReadOnly}
            versionId={currentVersion?.id}
            locationId={locationId ?? undefined}
            currency={locationCurrency}
            selectedYear={year}
            isRecalculating={isRefetching}
            onViewTransactions={(chartOfAccountId, accountName, accountNumber, month, columnType) => {
              setGlDetailModal({ chartOfAccountId, accountName, accountNumber, month, columnType });
            }}
            occupiedRoomsPerMonth={effectiveOccupiedRooms}
            availableRoomsPerMonth={effectiveAvailableRooms}
            isPoppedOut={panePoppedOut}
            onTogglePopout={togglePanePopout}
          />
        ) : showDept ? (
          <DepartmentDetailPanel
            departmentName={selectedDepartment.name}
            departmentId={selectedDepartment.departmentId}
            parentSection={selectedDepartment.parentSection}
            onClose={() => setSelectedDepartment(null)}
            versionId={currentVersion.id}
            locationId={locationId}
            currency={locationCurrency}
            isReadOnly={isReadOnly}
            onDriverSaved={handleDepartmentDriverSaved}
            onRevenueForecastSetup={selectedDepartment.parentSection === 'Revenue' && !isReadOnly ? () => {
              setRevenueForecastModal({ department: selectedDepartment.name, departmentId: selectedDepartment.departmentId });
            } : undefined}
            occupiedRoomsPerMonth={effectiveOccupiedRooms}
            isPoppedOut={panePoppedOut}
            onTogglePopout={togglePanePopout}
          />
        ) : null;
        if (!panePoppedOut) return pane;
        return (
          <>
            {pane && (
              <div className="w-[220px] border-l border-slate-200 bg-white flex flex-col items-center justify-center gap-3 p-4 text-center">
                <ExternalLink size={18} className="text-slate-400" />
                <div className="text-xs text-slate-500">Detail pane is open in a separate window</div>
                <button
                  type="button"
                  onClick={() => setPanePoppedOut(false)}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-lg border border-slate-200 px-3 py-1.5 hover:bg-slate-50"
                  style={{ color: 'var(--lodge-orange-500)' }}
                >
                  <PanelRightClose size={13} /> Bring it back
                </button>
              </div>
            )}
            <PopoutWindow
              open={panePoppedOut}
              title={`${selectedAccount?.accountName || selectedDepartment?.name || 'Detail'} · ${currentVersion?.name || 'Forecast'} ${year}`}
              onClosed={() => setPanePoppedOut(false)}
              emptyState={(
                <div className="flex-1 flex items-center justify-center p-8 text-center">
                  <div>
                    <div className="text-sm font-semibold text-slate-800 mb-1">Nothing selected</div>
                    <div className="text-xs text-slate-500">Select an account or department in the workspace and it will open here.</div>
                  </div>
                </div>
              )}
            >
              {pane}
              {/* Actions launched from the pane stay in the popout */}
              {orgId && locationId && glDetailModal && (
                <GLDetailModal
                  isOpen={true}
                  onClose={() => setGlDetailModal(null)}
                  orgId={orgId}
                  locationId={locationId}
                  chartOfAccountId={glDetailModal.chartOfAccountId}
                  accountName={glDetailModal.accountName}
                  accountNumber={glDetailModal.accountNumber}
                  year={year}
                  month={glDetailModal.month}
                  columnType={glDetailModal.columnType}
                  currency={locationCurrency}
                />
              )}
              {panePoppedOut && revenueForecastModal && currentVersion && locationId && orgId && (
                <RevenueForecastModal
                  isOpen={true}
                  onClose={() => setRevenueForecastModal(null)}
                  department={revenueForecastModal.department}
                  departmentId={revenueForecastModal.departmentId}
                  versionId={currentVersion.id}
                  locationId={locationId}
                  orgId={orgId}
                  year={year}
                  initialMonth={activeMonth ?? undefined}
                  initialTab={revenueForecastModal.initialTab}
                  isReadOnly={isReadOnly}
                  isBudget={isBudget}
                  onApplied={() => { refetch(); setRevenueForecastModal(null); }}
                  gridAvailableRooms={effectiveAvailableRooms}
                  currency={locationCurrency}
                />
              )}
              {popoutDailyEntry && currentVersion && locationId && (
                <DailyEntryModal
                  isOpen={true}
                  onClose={() => setPopoutDailyEntry(null)}
                  versionId={currentVersion.id}
                  chartOfAccountId={popoutDailyEntry.account.chartOfAccountId}
                  locationId={locationId}
                  accountName={popoutDailyEntry.account.accountName}
                  accountNumber={popoutDailyEntry.account.accountNumber || ''}
                  driverType={popoutDailyEntry.account.driverType}
                  driverValue={popoutDailyEntry.account.monthlyValues[MONTHS[popoutDailyEntry.month - 1]] || 0}
                  year={year}
                  month={popoutDailyEntry.month}
                  currency={locationCurrency}
                  onSave={() => { refetch(); }}
                />
              )}
            </PopoutWindow>
          </>
        );
      })()}

      {/* Bulk Action Toolbar */}
      {isBulkMode && (
        <BulkActionToolbar
          selectedCount={selectedCount}
          onEditDrivers={() => setIsBulkModalOpen(true)}
          onClearSelection={clearSelection}
          onSelectAllRevenue={() => handleSelectAccountsByType('Revenue')}
          onSelectAllExpense={() => handleSelectAccountsByType('Expense')}
          isLoading={bulkOperationInProgress || isBulkUpdating}
        />
      )}

      {/* Bulk Driver Edit Modal */}
      {orgId && locationId && (
        <BulkDriverEditModal
          isOpen={isBulkModalOpen}
          onClose={() => setIsBulkModalOpen(false)}
          selectedAccounts={selectedAccountObjects}
          onApply={handleBulkDriverApply}
          isLoading={bulkOperationInProgress || isBulkUpdating}
          orgId={orgId}
          locationId={locationId}
        />
      )}

      {/* GL Detail Modal */}
      {orgId && locationId && glDetailModal && !panePoppedOut && (
        <GLDetailModal
          isOpen={true}
          onClose={() => setGlDetailModal(null)}
          orgId={orgId}
          locationId={locationId}
          chartOfAccountId={glDetailModal.chartOfAccountId}
          accountName={glDetailModal.accountName}
          accountNumber={glDetailModal.accountNumber}
          year={year}
          month={glDetailModal.month}
          columnType={glDetailModal.columnType}
          currency={locationCurrency}
        />
      )}

      {/* Revenue Forecast Modal */}
      {/* Rooms revenue modal — hosted in the popout when launched there */}
      {(() => {
        const revenueModal = revenueForecastModal && currentVersion && orgId && locationId ? (
        <RevenueForecastModal
          isOpen={true}
          onClose={() => setRevenueForecastModal(null)}
          department={revenueForecastModal.department}
          departmentId={revenueForecastModal.departmentId}
          versionId={currentVersion.id}
          locationId={locationId}
          orgId={orgId}
          year={year}
          initialMonth={activeMonth ?? undefined}
          initialTab={revenueForecastModal.initialTab}
          isReadOnly={isReadOnly}
          isBudget={isBudget}
          onApplied={() => { refetch(); setRevenueForecastModal(null); }}
          gridAvailableRooms={effectiveAvailableRooms}
          currency={locationCurrency}
        />
        ) : null;
        return panePoppedOut ? null : revenueModal;
      })()}

      {/* Build with AI review takeover */}
      {showAiBuild && currentVersion && (
        <AiBuildReviewScreen
          versionId={currentVersion.id}
          currency={locationCurrency}
          isBudget={isBudget}
          onClose={() => setShowAiBuild(false)}
          onCommitted={handleAiBuildCommitted}
        />
      )}

      {/* Unlock Month Confirmation Modal */}
      {unlockConfirmMonth !== null && (
        <div className="fixed inset-0 flex items-center justify-center z-[9999]" style={{ background: 'rgba(15,15,17,.5)', backdropFilter: 'blur(2px)' }}>
          <div className="rounded-xl p-6 max-w-md mx-4" style={{ background: 'var(--lodge-white)', boxShadow: 'var(--shadow-xl)' }}>
            <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--lodge-onyx-800)' }}>
              Unlock {monthNames[unlockConfirmMonth - 1]}?
            </h3>
            <p className="text-sm mb-4" style={{ color: 'var(--lodge-onyx-600)' }}>
              Unlocking this month will delete the locked snapshot and allow editing.
              The forecast values will be recalculated based on current drivers and data.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setUnlockConfirmMonth(null)}
                disabled={isUnlocking}
                className="px-4 py-2 text-sm font-medium rounded-lg transition-colors"
                style={{ color: 'var(--lodge-onyx-600)' }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--lodge-onyx-50)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >
                Cancel
              </button>
              <button
                onClick={handleUnlockMonth}
                disabled={isUnlocking}
                className="px-4 py-2 text-sm font-semibold rounded-lg transition-all disabled:opacity-50"
                style={{ background: 'var(--lodge-orange-400)', color: '#ffffff', border: 'none', boxShadow: 'var(--shadow-orange)' }}
                onMouseEnter={e => { if (!isUnlocking) (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-orange-500)'; }}
                onMouseLeave={e => { if (!isUnlocking) (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-orange-400)'; }}
              >
                {isUnlocking ? 'Unlocking...' : 'Unlock Month'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WorkspacePage;
